import React, { useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import axiosInstance from "../../axiosInstance";
import { Pagination, Navigation, Autoplay, EffectFade } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-fade";
import { useBlogContext } from "../../fetchdata/BlogContext";

/* ─── Inline styles object (replaces scattered className CSS) ─────────────── */
const S = {
  // Gold/luxury palette
  gold: "#B8892A",
  goldLight: "#D4AA50",
  goldDark: "#8C6518",
  cream: "#FAF7F2",
  charcoal: "#1A1814",
  warmGray: "#6B6560",

  // Utility
  sectionPad: { padding: "72px 0" },
  container: { maxWidth: 1500, margin: "0 auto", padding: "0 24px" },
};

/* ─── Reusable luxury section heading ───────────────────────────────────────── */
const SectionHeading = ({ eyebrow, title, center = false }) => (
  <div style={{ textAlign: center ? "center" : "left", marginBottom: 48 }}>
    {eyebrow && (
      <p style={{
        fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase",
        color: S.gold,
        fontWeight: 600, marginBottom: 10,
      }}>
        {eyebrow}
      </p>
    )}
    <h2 style={{
      fontSize: "clamp(28px, 4vw, 42px)",

      fontWeight: 400, color: S.charcoal,
      lineHeight: 1.2, margin: 0,
      letterSpacing: "-0.01em",
    }}>
      {title}
    </h2>
    <div style={{
      width: 48, height: 1.5, background: S.gold,
      margin: center ? "20px auto 0" : "20px 0 0",
    }} />
  </div>
);

/* ─── Product Card ───────────────────────────────────────────────────────────── */
const ProductCard = ({ product }) => {
  const [hovered, setHovered] = useState(false);
  const isVideo = (url) => /\.(mp4|webm|ogg)$/i.test(url);
  const discount = product.regularPrice && product.salePrice
    ? Math.round(((product.regularPrice - product.salePrice) / product.regularPrice) * 100)
    : null;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "box-shadow 0.35s ease",
        boxShadow: hovered ? "0 20px 60px rgba(26,24,20,0.12)" : "0 2px 12px rgba(26,24,20,0.06)",
        position: "relative",
      }}
    >
      {/* Image */}
      <div style={{ position: "relative", aspectRatio: "3/4", overflow: "hidden", background: "#F5F0E8" }}>
        {isVideo(product.pImage) ? (
          <video src={product.pImage} autoPlay muted loop playsInline
            style={{
              width: "100%", height: "100%", objectFit: "cover",
              transform: hovered ? "scale(1.06)" : "scale(1)", transition: "transform 0.6s ease"
            }} />
        ) : (
          <img src={product.pImage} alt={product.title}
            style={{
              width: "100%", height: "100%", objectFit: "cover",
              transform: hovered ? "scale(1.06)" : "scale(1)", transition: "transform 0.6s ease"
            }} />
        )}

        {/* Discount badge */}
        {discount && (
          <div style={{
            position: "absolute", top: 16, left: 16,
            background: S.charcoal, color: "#fff",
            fontSize: 11, letterSpacing: "0.1em",
            padding: "5px 10px",
          }}>
            {discount}% OFF
          </div>
        )}

        {/* Quick view overlay */}
        <div style={{
          position: "absolute", inset: 0,
          background: "rgba(26,24,20,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          opacity: hovered ? 1 : 0, transition: "opacity 0.3s ease",
        }}>
          <Link to={`/product/${product.slug}`} style={{
            padding: "12px 32px",
            border: "1px solid rgba(255,255,255,0.8)",
            color: "#fff", fontSize: 12, letterSpacing: "0.18em",
            textTransform: "uppercase", textDecoration: "none",

            background: "transparent",
            transition: "background 0.2s",
          }}>
            View Details
          </Link>
        </div>

        {/* Wishlist */}
        <button style={{
          position: "absolute", top: 14, right: 14,
          width: 36, height: 36, borderRadius: "50%",
          background: "rgba(255,255,255,0.9)", border: "none",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          opacity: hovered ? 1 : 0, transition: "opacity 0.3s ease",
        }}>
          <svg width={16} height={16} viewBox="0 0 20 20" fill="none">
            <path d="M17.3666 3.84172C16.941 3.41589 16.4356 3.0781 15.8794 2.84763C15.3232 2.61716 14.727 2.49854 14.1249 2.49854C13.5229 2.49854 12.9267 2.61716 12.3705 2.84763C11.8143 3.0781 11.3089 3.41589 10.8833 3.84172L9.99994 4.72506L9.1166 3.84172C8.25686 2.98198 7.0908 2.49898 5.87494 2.49898C4.65907 2.49898 3.49301 2.98198 2.63327 3.84172C1.77353 4.70147 1.29053 5.86753 1.29053 7.08339C1.29053 8.29925 1.77353 9.46531 2.63327 10.3251L3.5166 11.2084L9.99994 17.6917L16.4833 11.2084L17.3666 10.3251C17.7924 9.89943 18.1302 9.39407 18.3607 8.83785C18.5912 8.28164 18.7098 7.68546 18.7098 7.08339C18.7098 6.48132 18.5912 5.88514 18.3607 5.32893C18.1302 4.77271 17.7924 4.26735 17.3666 3.84172Z"
              stroke={S.charcoal} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Info */}
      <div style={{ padding: "20px 20px 24px" }}>
        <h5 style={{
          fontWeight: 400,
          fontSize: 16, color: S.charcoal, margin: "0 0 8px",
          lineHeight: 1.4,
        }}>
          <Link to={`/product/${product.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
            {product.title || "Handcrafted Jewel"}
          </Link>
        </h5>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: S.gold }}>
            ₹{product.salePrice?.toLocaleString()}
          </span>
          {product.regularPrice && (
            <span style={{ fontSize: 14, color: S.warmGray, textDecoration: "line-through" }}>
              ₹{product.regularPrice?.toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Home Component ────────────────────────────────────────────────────── */
const Home = () => {
  const {
    isLayoutLoading, layout, Headers, isProducts,
    AllBlogs, IsBlogLoading,
  } = useBlogContext();

  const swiperRefLocal = useRef();
  const [goldRates, setGoldRates] = useState({});
  const [silverPrice, setSilverPrice] = useState(null);
  const [Products, setProducts] = useState([]);
  const location = useLocation();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const gold10gm = Headers.gold;
    const silver10gm = Headers.sliver;
    const goldPerGram = gold10gm / 10;
    const silverPerGram = silver10gm / 10;
    setGoldRates({
      "24KT": goldPerGram,
      "22KT": goldPerGram * (22 / 24),
      "18KT": goldPerGram * (18 / 24),
      "14KT": goldPerGram * (14 / 24),
    });
    setSilverPrice(silverPerGram);
  }, [Headers]);

  useEffect(() => {
    axiosInstance.get("/all-home-products")
      .then(({ data }) => setProducts(data.products))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [location]);

  const isVideo = (url) => /\.(mp4|webm|ogg)$/i.test(url);
  const trendingProducts = Products.filter(p => layout?.trending_product?.includes(p._id));
  const bestSelling = Products.filter(p => layout?.best_selling_laptop?.includes(p._id));

  /* Google Fonts injection */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=Cormorant+Garamond:wght@300;400;500;600&family=Jost:wght@300;400;500&display=swap";
    document.head.appendChild(link);
  }, []);

  return (
    <>
      {/* Google Fonts */}
      <style>{`
        * { font-family: 'Jost', sans-serif; }
        .lux-hero-swiper .swiper-pagination-bullet { background: rgba(255,255,255,0.5); width: 6px; height: 6px; }
        .lux-hero-swiper .swiper-pagination-bullet-active { background: #B8892A; width: 24px; border-radius: 3px; }
        .cat-swiper .swiper-button-next, .cat-swiper .swiper-button-prev { color: #B8892A; --swiper-navigation-size: 18px; }
        .prod-swiper .swiper-pagination-bullet { background: #1A1814; opacity: 0.25; }
        .prod-swiper .swiper-pagination-bullet-active { background: #B8892A; opacity: 1; }
        .ticker-wrap { overflow: hidden; white-space: nowrap; }
        .ticker-inner { display: inline-flex; animation: ticker 18s linear infinite; }
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .review-card:hover { transform: translateY(-4px); }
        .review-card { transition: transform 0.3s ease; }
        .blog-card-img { transition: transform 0.5s ease; }
        .blog-card:hover .blog-card-img { transform: scale(1.05); }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.7s ease forwards; }
        .fade-up-2 { animation: fadeUp 0.7s 0.15s ease forwards; opacity: 0; }
        .fade-up-3 { animation: fadeUp 0.7s 0.3s ease forwards; opacity: 0; }
      `}</style>

      <Header />

      <main style={{ background: S.cream, overflowX: "hidden" }}>

        {/* ── TICKER BAR ──────────────────────────────────────────────── */}
        <div style={{ background: 'black', padding: "9px 0" }}>
          <div className="ticker-wrap">
            <div className="ticker-inner">
              {[
                `✦  Gold 24KT: ₹${goldRates["24KT"] ? Math.floor(goldRates["24KT"]) : "—"}/gm`,
                `✦  Gold 22KT: ₹${goldRates["22KT"] ? Math.floor(goldRates["22KT"]) : "—"}/gm`,
                `✦  Gold 18KT: ₹${goldRates["18KT"] ? Math.floor(goldRates["18KT"]) : "—"}/gm`,
                `✦  Silver: ₹${silverPrice ? Math.floor(silverPrice) : "—"}/gm`,
                `✦  BIS Hallmarked Jewellery  ✦  Free Shipping Above ₹5,000  ✦  30-Day Easy Returns`,
              ].concat([
                `✦  Gold 24KT: ₹${goldRates["24KT"] ? Math.floor(goldRates["24KT"]) : "—"}/gm`,
                `✦  Gold 22KT: ₹${goldRates["22KT"] ? Math.floor(goldRates["22KT"]) : "—"}/gm`,
                `✦  Gold 18KT: ₹${goldRates["18KT"] ? Math.floor(goldRates["18KT"]) : "—"}/gm`,
                `✦  Silver: ₹${silverPrice ? Math.floor(silverPrice) : "—"}/gm`,
                `✦  BIS Hallmarked Jewellery  ✦  Free Shipping Above ₹5,000  ✦  30-Day Easy Returns`,
              ]).map((item, i) => (
                <span key={i} style={{
                  fontSize: 11, letterSpacing: "0.18em", color: "rgba(255,255,255,0.75)",
                  marginRight: 56, fontWeight: 500,
                }}>
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── HERO SLIDER ─────────────────────────────────────────────── */}
        <section style={{ position: "relative" }}>
          <Swiper
            effect="fade"
            slidesPerView={1}
            pagination={{ clickable: true }}
            autoplay={{ delay: 3500, pauseOnMouseEnter: true }}
            loop
            modules={[Pagination, EffectFade, Autoplay]}
            className="lux-hero-swiper"
          >
            {isLayoutLoading
              ? [1, 2].map(i => (
                <SwiperSlide key={i}>
                  <div style={{ aspectRatio: "1800/675", background: "#E8E0D0", borderRadius: 0 }} />
                </SwiperSlide>
              ))
              : layout?.home_slider?.map((image, index) => (
                <SwiperSlide key={index}>
                  <div style={{ position: "relative", aspectRatio: "1800/675", overflow: "hidden" }}>
                    <img src={image} alt="Gopal Jewels" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    {/* Subtle dark gradient at bottom for text readability */}
                    <div style={{
                      position: "absolute", inset: 0,
                      background: "linear-gradient(to top, rgba(26,24,20,0.55) 0%, transparent 55%)",
                    }} />
                    {/* Hero caption */}
                    <div style={{
                      position: "absolute", bottom: "10%", left: "6%",
                      color: "#fff",
                    }}>


                      <Link className="fade-up-3" to="/shop" style={{
                        display: "inline-block",
                        padding: "13px 36px",
                        background: S.gold, color: "#fff",

                        fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase",
                        textDecoration: "none",
                        transition: "background 0.25s",
                      }}>
                        Explore Collection
                      </Link>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>
        </section>

        {/* ── TRUST STRIP ─────────────────────────────────────────────── */}
        <section style={{ background: "#fff", borderTop: `1px solid rgba(184,137,42,0.2)`, borderBottom: `1px solid rgba(184,137,42,0.2)` }}>
          <div style={{ ...S.container, display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
            {[
              { icon: "✦", label: "BIS Hallmarked", sub: "Certified Purity" },
              { icon: "✧", label: "Free Shipping", sub: "Orders above ₹5,000" },
              { icon: "◈", label: "Easy Returns", sub: "30-Day Policy" },
              { icon: "✦", label: "Lifetime Service", sub: "Complimentary Polish" },
            ].map((t, i) => (
              <div key={i} style={{
                flex: "1 1 180px", textAlign: "center",
                padding: "28px 16px",
                borderRight: i < 3 ? "1px solid rgba(184,137,42,0.15)" : "none",
              }}>
                <span style={{ fontSize: 22, color: S.gold, display: "block", marginBottom: 8 }}>{t.icon}</span>
                <p style={{ fontSize: 13, fontWeight: 500, color: S.charcoal, margin: "0 0 3px", letterSpacing: "0.04em" }}>{t.label}</p>
                <p style={{ fontSize: 11, color: S.warmGray, margin: 0 }}>{t.sub}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── SHOP BY CATEGORY ────────────────────────────────────────── */}
        <section style={{ ...S.sectionPad, background: S.cream }}>
          <div style={S.container}>
            <SectionHeading eyebrow="Explore" title="Shop By Category" />
            <Swiper
              slidesPerView={2.4}
              spaceBetween={16}
              autoplay={{ delay: 1800, pauseOnMouseEnter: true }}
              navigation
              loop
              breakpoints={{
                480: { slidesPerView: 3 },
                768: { slidesPerView: 4 },
                1024: { slidesPerView: 6 },
              }}
              modules={[Autoplay, Navigation]}
              className="cat-swiper"
            >
              {isLayoutLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                  <SwiperSlide key={i}>
                    <div style={{ aspectRatio: "1/1", borderRadius: "50%", background: "#E8E0D0" }} />
                  </SwiperSlide>
                ))
                : layout?.trending_product_banner?.map((item, index) => (
                  <SwiperSlide key={index}>
                    <Link to={item?.imageUrlInput} style={{ textDecoration: "none" }}>
                      <div style={{ textAlign: "center" }}>
                        {/* Circle image */}
                        <div style={{
                          width: "100%", aspectRatio: "1/1",
                          borderRadius: "50%", overflow: "hidden",
                          border: `2px solid rgba(184,137,42,0.3)`,
                          boxShadow: "0 4px 20px rgba(26,24,20,0.08)",
                          transition: "box-shadow 0.3s",
                          marginBottom: 14,
                          background: "#F5F0E8",
                        }}>
                          <img src={item?.imageInput} alt={item?.imageTITInput}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                        <p style={{

                          fontSize: 14, fontWeight: 500, color: S.charcoal,
                          margin: 0, letterSpacing: "0.03em",
                        }}>
                          {item?.imageTITInput}
                        </p>
                        {item?.itemCount && (
                          <p style={{ fontSize: 11, color: S.gold, margin: "3px 0 0" }}>{item.itemCount} pieces</p>
                        )}
                      </div>
                    </Link>
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </section>

        {/* ── PROMO BANNERS (trending_product_carousal) ───────────────── */}
        {!isLayoutLoading && layout?.trending_product_carousal?.length > 0 && (
          <section style={{ padding: "0 0 56px" }}>
            <div style={S.container}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
                {layout.trending_product_carousal.map((image, i) => (
                  <div key={i} style={{ borderRadius: 4, overflow: "hidden" }}>
                    <img src={image?.imageInput} alt="promo" style={{ width: "100%", display: "block" }} />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── TRENDING PRODUCTS ────────────────────────────────────────── */}
        <section style={{ ...S.sectionPad, background: "#fff" }}>
          <div style={S.container}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 48 }}>
              <SectionHeading eyebrow="Curated For You" title="Trending Now" />
              <Link to="/shop" style={{
                fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase",
                color: S.gold, textDecoration: "none",
                fontWeight: 600, borderBottom: `1px solid ${S.gold}`, paddingBottom: 2,
              }}>
                View All
              </Link>
            </div>
            <Swiper
              breakpoints={{
                300: { slidesPerView: 2, spaceBetween: 12 },
                640: { slidesPerView: 2, spaceBetween: 16 },
                768: { slidesPerView: 3, spaceBetween: 20 },
                1024: { slidesPerView: 4, spaceBetween: 24 },
              }}
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className="prod-swiper"
              style={{ paddingBottom: 48 }}
            >
              {isProducts
                ? Array.from({ length: 4 }).map((_, i) => (
                  <SwiperSlide key={i}>
                    <div style={{ aspectRatio: "3/4", background: "#E8E0D0", borderRadius: 2 }} />
                  </SwiperSlide>
                ))
                : trendingProducts.map((product, i) => (
                  <SwiperSlide key={i}>
                    <ProductCard product={product} />
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </section>

        {/* ── EDITORIAL BANNER (full-width marquee) ───────────────────── */}
        <section style={{ background: S.charcoal, padding: "56px 0", textAlign: "center" }}>
          <div style={S.container}>
            <p style={{
              fontSize: "clamp(20px, 3.5vw, 36px)",
              color: "rgba(255,255,255,0.92)", fontWeight: 400, fontStyle: "italic",
              lineHeight: 1.5, margin: "0 0 28px",
            }}>
              "Every piece tells a story — of heritage, craftsmanship, and love."
            </p>
            <div style={{ width: 48, height: 1, background: S.gold, margin: "0 auto 28px" }} />
            <Link to="/about" style={{
              fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase",
              color: S.goldLight, textDecoration: "none",
              fontWeight: 600,
            }}>
              Our Story →
            </Link>
          </div>
        </section>

        {/* ── LATEST PRODUCT BANNER (Swiper) ──────────────────────────── */}
        {!isLayoutLoading && layout?.latest_product_banner?.length > 0 && (
          <section style={{ ...S.sectionPad, background: S.cream }}>
            <div style={S.container}>
              <SectionHeading eyebrow="New Arrivals" title="Just Landed" />
              <Swiper
                slidesPerView={2}
                spaceBetween={16}
                autoplay={{ delay: 1500, pauseOnMouseEnter: true }}
                loop
                breakpoints={{
                  768: { slidesPerView: 3 },
                  1024: { slidesPerView: 4 },
                }}
                modules={[Autoplay]}
              >
                {layout.latest_product_banner.map((image, i) => (
                  <SwiperSlide key={i}>
                    <Link to={image?.imageUrlInput}>
                      <div style={{ borderRadius: 4, overflow: "hidden", aspectRatio: "1/1" }} className="blog-card">
                        <img src={image?.imageInput} alt="" className="blog-card-img"
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      </div>
                    </Link>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </section>
        )}

        {/* ── BEST SELLING ─────────────────────────────────────────────── */}
        <section style={{ ...S.sectionPad, background: "#FDFAF4" }}>
          <div style={S.container}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 48 }}>
              <SectionHeading eyebrow="Most Loved" title="Best Sellers" />
              <Link to="/shop" style={{
                fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase",
                color: S.gold, textDecoration: "none",
                fontWeight: 600, borderBottom: `1px solid ${S.gold}`, paddingBottom: 2,
              }}>
                Shop All
              </Link>
            </div>
            <Swiper
              breakpoints={{
                300: { slidesPerView: 2, spaceBetween: 12 },
                640: { slidesPerView: 2, spaceBetween: 16 },
                768: { slidesPerView: 3, spaceBetween: 20 },
                1024: { slidesPerView: 4, spaceBetween: 24 },
              }}
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className="prod-swiper"
              style={{ paddingBottom: 48 }}
            >
              {isProducts
                ? Array.from({ length: 4 }).map((_, i) => (
                  <SwiperSlide key={i}>
                    <div style={{ aspectRatio: "3/4", background: "#E8E0D0", borderRadius: 2 }} />
                  </SwiperSlide>
                ))
                : bestSelling.map((product, i) => (
                  <SwiperSlide key={i}>
                    <ProductCard product={product} />
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </section>

        {/* ── GOLD & SILVER RATES CARD ─────────────────────────────────── */}
        <section style={{ padding: "64px 0", background: "#fff" }}>
          <div style={S.container}>
            <SectionHeading eyebrow="Live Prices" title="Today's Metal Rates" center />
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 2, border: `1px solid rgba(184,137,42,0.25)`,
            }}>
              {[
                { label: "Gold 24KT", value: goldRates["24KT"] ? `₹${Math.floor(goldRates["24KT"])}` : "—", sub: "per gram" },
                { label: "Gold 22KT", value: goldRates["22KT"] ? `₹${Math.floor(goldRates["22KT"])}` : "—", sub: "per gram" },
                { label: "Gold 18KT", value: goldRates["18KT"] ? `₹${Math.floor(goldRates["18KT"])}` : "—", sub: "per gram" },
                { label: "Gold 14KT", value: goldRates["14KT"] ? `₹${Math.floor(goldRates["14KT"])}` : "—", sub: "per gram" },
                { label: "Silver 999", value: silverPrice ? `₹${Math.floor(silverPrice)}` : "—", sub: "per gram", silver: true },
              ].map((r, i) => (
                <div key={i} style={{
                  padding: "32px 20px", textAlign: "center",
                  background: r.silver ? S.charcoal : "#FAF8F3",
                  borderRight: "1px solid rgba(184,137,42,0.15)",
                }}>
                  <p style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: r.silver ? "rgba(255,255,255,0.55)" : S.warmGray, margin: "0 0 12px" }}>
                    {r.label}
                  </p>
                  <p style={{ fontSize: 26, fontWeight: 400, color: r.silver ? "#C8C8C8" : S.gold, margin: "0 0 6px" }}>
                    {r.value}
                  </p>
                  <p style={{ fontSize: 11, color: r.silver ? "rgba(255,255,255,0.4)" : "#B0A89A", margin: 0 }}>{r.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── REVIEWS ──────────────────────────────────────────────────── */}
        <section style={{ ...S.sectionPad, background: S.cream }} id="reviews">
          <div style={S.container}>
            <SectionHeading eyebrow="Testimonials" title="What Our Customers Say" center />
            <Swiper
              slidesPerView={1}
              spaceBetween={24}
              loop
              autoplay={{ delay: 3000, pauseOnMouseEnter: true }}
              breakpoints={{ 640: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
              modules={[Autoplay]}
            >
              {[
                { name: "Priya Sharma", city: "Mumbai", text: "The craftsmanship is extraordinary. My wedding necklace from Gopal Jewels was the highlight of the ceremony.", rating: 5 },
                { name: "Arvind Das", city: "Delhi", text: "Exceptional quality and service. The hallmark certification gives me complete confidence in every purchase.", rating: 5 },
                { name: "Sunita Patel", city: "Ahmedabad", text: "Bought earrings for my daughter's engagement — everyone at the party asked where they were from!", rating: 5 },
                { name: "Rekha Nair", city: "Kochi", text: "Beautiful packaging, authentic gold, and lightning-fast delivery. Will always return to Gopal Jewels.", rating: 5 },
                { name: "Meena Gupta", city: "Jaipur", text: "The stone setting on my ring is flawless. True artisans at work. Highly recommend their range.", rating: 5 },
              ].map((r, i) => (
                <SwiperSlide key={i}>
                  <div className="review-card" style={{
                    background: "#fff", padding: "32px 28px",
                    border: `1px solid rgba(184,137,42,0.18)`,
                    height: "100%", boxSizing: "border-box",
                  }}>
                    {/* Stars */}
                    <div style={{ marginBottom: 16, color: S.gold, fontSize: 13, letterSpacing: 2 }}>
                      {"★".repeat(r.rating)}
                    </div>
                    <p style={{
                      fontSize: 17,
                      fontStyle: "italic", color: S.charcoal, lineHeight: 1.7,
                      margin: "0 0 24px",
                    }}>
                      "{r.text}"
                    </p>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: "50%",
                        background: `linear-gradient(135deg, ${S.gold}, ${S.goldDark})`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 14, fontWeight: 500, color: "#fff",

                      }}>
                        {r.name[0]}
                      </div>
                      <div>
                        <p style={{ fontSize: 14, fontWeight: 500, color: S.charcoal, margin: 0 }}>{r.name}</p>
                        <p style={{ fontSize: 11, color: S.warmGray, margin: 0 }}>{r.city} · Verified Buyer</p>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>

        {/* ── ALL BLOGS ─────────────────────────────────────────────────── */}
        {!isLayoutLoading && layout?.how_works?.length > 0 && (
          <section style={{ ...S.sectionPad, background: "#fff" }}>
            <div style={S.container}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 48 }}>
                <SectionHeading eyebrow="Stories" title="From Our Journal" />
                <Link to="/blogs" style={{
                  fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase",
                  color: S.gold, textDecoration: "none",
                  fontWeight: 600, borderBottom: `1px solid ${S.gold}`, paddingBottom: 2,
                }}>
                  All Articles
                </Link>
              </div>
              <Swiper
                slidesPerView={1.2}
                spaceBetween={20}
                loop
                autoplay={{ delay: 2500, pauseOnMouseEnter: true }}
                breakpoints={{ 640: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
                modules={[Autoplay]}
              >
                {layout.how_works.map((image, i) => (
                  <SwiperSlide key={i}>
                    <Link to={image?.paragraph} style={{ textDecoration: "none" }} className="blog-card">
                      <div style={{ background: S.cream, borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ aspectRatio: "4/3", overflow: "hidden" }}>
                          <img src={image?.icon} alt={image?.heading} className="blog-card-img"
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        </div>
                        <div style={{ padding: "24px 20px" }}>
                          <p style={{ fontSize: 10, letterSpacing: "0.2em", color: S.gold, textTransform: "uppercase", margin: "0 0 10px", }}>
                            Jewellery Tips
                          </p>
                          <h4 style={{
                            fontWeight: 400,
                            fontSize: 18, color: S.charcoal, margin: "0 0 12px", lineHeight: 1.4,
                          }}>
                            {image?.heading}
                          </h4>
                          <span style={{ fontSize: 12, color: S.gold, letterSpacing: "0.12em", }}>
                            Read More →
                          </span>
                        </div>
                      </div>
                    </Link>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </section>
        )}

        {/* {!isLayoutLoading && layout?.service_category_Images?.length > 0 && (
          <section style={{ padding: "48px 0 0" }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <p style={{ fontSize: 11, letterSpacing: "0.25em", color: S.gold, textTransform: "uppercase", marginBottom: 8 }}>
                Follow Us
              </p>
              <h3 style={{ fontWeight: 400, fontSize: 28, color: S.charcoal, margin: 0 }}>
                @GopалJewels on Instagram
              </h3>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(layout.service_category_Images.length, 6)}, 1fr)`, gap: 0 }}>
              {layout.service_category_Images.slice(0, 6).map((item, i) => (
                <a key={i} href={item.imageUrlInput || "#"} target="_blank" rel="noreferrer"
                  style={{ display: "block", aspectRatio: "1/1", overflow: "hidden" }} className="blog-card">
                  <img src={item.imageInput} alt="" className="blog-card-img"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </a>
              ))}
            </div>
          </section>
        )} */}

      </main>

      <Footer />
    </>
  );
};

export default Home;